import { Spacing } from '@persona-id/design-tokens';
import { get, noop, omit, set, uniq } from 'lodash/fp';
import { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css, ThemeProvider } from 'styled-components';

import { getScreenDimensionsLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/selectors/screenDimensions';
import { ScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  findComponentConfigPath,
  isTerminalStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { ScreenComponent } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenComponent';
import { ScreenDropPreview } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenDropPreview';
import {
  DraggableHoverPosition,
  ScreenEditorComponentItem,
  ScreenEditorDraggableItemTypes,
  SelectedComponentAndStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import {
  addComponentsAtIndex,
  cancelButtonEnabledForStep,
  createComponentConfigAndFields,
  createMockInquiryPropsForUiComponentEditor,
  determineComponentShiftIndex,
  getStepComponentConfigs,
  isComponentFromStep,
  isCurrentComponentBIPASubmitTrigger,
  isCurrentComponentBIPAText,
  isHeaderComponentVisible,
  isInsertingComponentBetweenBIPAComponents,
  isShiftingComponentUp,
  removeComponentFromPath,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import { DragAndDropProvider } from 'dashboard/components/DragAndDropProvider';
import ThemedFonts from 'dashboard/components/ThemedFonts';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { refreshComponentPreview } from 'dashboard/redux/thunk-actions/inquiry-template';
import { Elevation } from 'design-system/variables/effect';
import { FieldSchema } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { ApiModel } from 'lib/interfaces/api';
import { StepPreview, StepUiConfig } from 'lib/interfaces/StepsConfig';
import {
  isUiParentComponent,
  isUiParentComponentConfig,
  isUiTransitionComponentConfig,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
  UiComponentType,
} from 'lib/interfaces/ui-components';
import Navbar from 'verify/components/Navbar/Navbar';
import { serializeTheme } from 'verify/lib/theme';
import { ThemeAttributes } from 'verify/models/theme';

const ScreenEditorDroppableScreenContainer = styled.div.attrs<{
  screenDimensions: ScreenDimensions;
}>((props) => ({
  style: {
    width: `${props.screenDimensions.width}px`,
    height: `${props.screenDimensions.height}px`,
  },
}))<{
  selected: boolean;
  backgroundColor: string;
  startedComponentDrag: boolean;
  disableDrop: boolean;
  isOver: boolean;
  screenDimensions: ScreenDimensions;
}>`
  display: grid;
  border-radius: ${({ theme }) => theme.borderRadiusModal};
  box-shadow: ${Elevation.High};
  background-color: ${({ backgroundColor }) => `${backgroundColor}`};
  position: relative;
  overflow-y: scroll;
  overflow-x: hidden;
  outline: ${({ selected, isOver, startedComponentDrag }) =>
    selected || isOver || startedComponentDrag ? `2px solid` : ''};
  outline-color: ${({ disableDrop, theme }) =>
    disableDrop ? theme.colorError : theme.color.primary.brand600};
`;

const ScreenDropzone = styled.div<{ isOver: boolean }>`
  height: 100%;
`;

const ScreenComponentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[8]} ${Spacing[5]} ${Spacing[5]} ${Spacing[5]};
  flex-grow: 1;
  overflow: hidden;
  z-index: 1;
`;

const ScreenDropzoneInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const NavbarContainer = styled.div<{ selected?: boolean; isHovering?: boolean }>`
  position: sticky;
  top: 0;
  margin-bottom: -${Spacing[7]};
  z-index: 1;

  ${({ selected, theme }) =>
    selected &&
    css`
      outline-color: ${theme.color.primary.brand700};
      outline-style: solid;
      outline-width: 1px;
    `}

  ${({ isHovering, theme }) =>
    isHovering &&
    css`
      outline-style: solid;
      outline-color: ${theme.color.primary.brand200};
      outline-width: 1px;
    `}

  nav {
    position: relative;

    // when the navbar is rendered, disable interactions for all child elements
    // this is to prevent the screen from refreshing when buttons in nav component
    // are clicked (due to nav buttons in this component being of type='submit')
    & > * {
      pointer-events: none !important;
    }
  }
`;

const ScreenIndicator = styled.div<{ side: DraggableHoverPosition; isOver: boolean }>`
  ${({ side }) => {
    switch (side) {
      case 'top':
        return 'top: 47px; left: 0px; width: 100%; height: 1px;';
      case 'bottom':
        return 'bottom: 23px; left: 0px; width: 100%; height: 1px;';
      case 'left':
        return 'left: 23px; top: 0px; height: 100%; width: 1px;';
      case 'right':
        return 'right: 23px; top: 0px; height: 100%; width: 1px;';
      default:
        return null;
    }
  }}
  display: ${({ isOver }) => (isOver ? 'block' : 'none')};
  position: absolute;
  background: ${({ theme }) => theme.color.primary.brand600};
  z-index: 0;
`;

type ScreenEditorDroppableScreenContainerProps = {
  step?: InquiryTemplateVersionStepUi;
  stepPreview?: StepPreview;
  fieldSchema: FieldSchema[];
  initialFieldSchema: FieldSchema[];
  selectedComponentAndStep: SelectedComponentAndStep;
  theme?: ThemeAttributes;
  fallbackScreen?: ReactNode;
  editable: boolean;
  isUiStepScreen: boolean;
  selected: boolean;
  startedComponentDrag: boolean;
  onComponentClick: (value: SelectedComponentAndStep) => void;
  updateSteps: ({
    updatedStep,
    component,
    fields,
  }: {
    updatedStep: InquiryTemplateVersionStep;
    component?: UiComponentConfig;
    fields?: FieldSchema[];
  }) => void;
};

export const ScreenEditorDroppableScreen = memo(
  ({
    step,
    stepPreview,
    fieldSchema,
    initialFieldSchema,
    selectedComponentAndStep,
    theme,
    fallbackScreen,
    editable,
    isUiStepScreen,
    selected,
    startedComponentDrag,
    onComponentClick,
    updateSteps,
  }: ScreenEditorDroppableScreenContainerProps) => {
    const [hoveredIndex, setHoveredIndex] = useState<string[] | null>(null);
    const [isHovering, { enable: setIsHovering, disable: setStopHovering }] = useToggle(false);
    const [hoverPosition, setHoverPosition] = useState<DraggableHoverPosition>(null);
    const dispatch = useDispatch();
    const screenDimensions = useSelector<RootState, ScreenDimensions>((state: RootState) =>
      getScreenDimensionsLiveUpdated(state),
    );

    const components = useMemo(
      () =>
        isUiStepScreen && stepPreview && step != null
          ? (stepPreview.config as StepUiConfig).components
          : null,
      [isUiStepScreen, stepPreview, step],
    );

    const [{ isOver, isOverCurrent, componentItem }, dropRef] = useDrop<
      ScreenEditorComponentItem,
      void,
      { isOver: boolean; isOverCurrent: boolean; componentItem: ScreenEditorComponentItem }
    >(
      () => ({
        accept: ScreenEditorDraggableItemTypes.COMPONENT,
        collect: (monitor) => ({
          isOver: monitor.isOver(),
          isOverCurrent: monitor.isOver({
            shallow: true,
          }),
          componentItem: monitor.getItem(),
        }),
        hover: (_, monitor) => {
          // if we stop moving any components on the screen, we want to unset the hoveredIndex
          if (monitor.isOver({ shallow: true }) && hoveredIndex != null) {
            setHoveredIndex(null);
          }
        },
        canDrop: () => editable,
        drop: (item: ScreenEditorComponentItem, monitor) => {
          if (!step) return;

          if (!isUiStepScreen) {
            dispatch(
              showMessage(
                'This screen is part of a preconfigured flow. You cannot add components to this screen.',
                MessageType.Failure,
              ),
            );
            return;
          }

          // checking to see if the drop happened within the main container
          // or a component that can accept children
          const didDrop = monitor.didDrop();
          if (didDrop) return;

          // limit copying over components from different screens

          if (step && !isComponentFromStep(item, step)) {
            dispatch(
              showMessage('Cannot copy components from different screens', MessageType.Failure),
            );
            return;
          }

          let updatedStep: InquiryTemplateVersionStepUi | null = null;
          let newComponentConfig: UiComponentConfig | null = null;
          let newComponentFields: FieldSchema[] = [];

          // check to see if the component already exists within drop target
          // and the position has changed
          const originalComponentPath = findComponentConfigPath(
            (component: UiComponentConfig) => component.name === item?.name,

            step.config.components,
          );

          const hoveredComponentConfig = hoveredIndex && get(hoveredIndex, step.config.components);

          // if not, do nothing
          if (hoveredComponentConfig != null && originalComponentPath === hoveredIndex) {
            return;
          }

          // disallow moving BIPA submit triggers
          if (
            originalComponentPath != null &&
            (isCurrentComponentBIPASubmitTrigger({ step, component: item as UiComponentConfig }) ||
              isCurrentComponentBIPAText({ step, component: item as UiComponentConfig }))
          ) {
            dispatch(
              showMessage('Cannot move component due to BIPA compliance', MessageType.Failure),
            );
            return;
          }

          const { nonFooterComponentsConfig, footerComponentConfig } =
            getStepComponentConfigs(step);

          if (item.type === UiComponentConfigType.Footer && footerComponentConfig.length > 0) {
            dispatch(showMessage('Screen already contains a footer component'));
            return;
          }

          const isShiftingUp = isShiftingComponentUp(hoverPosition);
          const defaultShiftIndex = isShiftingUp ? 0 : nonFooterComponentsConfig.length;

          const firstOriginalComponentPath = originalComponentPath?.at(0);
          const firstHoveredIndex = hoveredIndex?.at(0);

          if (firstOriginalComponentPath) {
            const originalComponentIndex = parseInt(firstOriginalComponentPath);
            // don't shift at all if this is the top component and there's no shift
            if (originalComponentIndex === 0 && hoverPosition == null) {
              return;
            }
            const originalComponentConfig = omit('stepName', item) as UiComponentConfig;
            // update steps

            updatedStep = removeComponentFromPath(originalComponentConfig, step);

            let updateIndex = defaultShiftIndex;

            if (firstHoveredIndex) {
              updateIndex = determineComponentShiftIndex({
                originalComponentIndex: parseInt(firstOriginalComponentPath),

                hoverComponentIndex: parseInt(firstHoveredIndex),
                hoverPosition,
              });
            }

            if (
              (isUiParentComponentConfig(item as UiComponentConfig) ||
                isUiTransitionComponentConfig(item as UiComponentConfig)) &&
              isInsertingComponentBetweenBIPAComponents({ step, index: updateIndex })
            ) {
              dispatch(
                showMessage(
                  'Cannot insert another button between BIPA text and submit button',
                  MessageType.Failure,
                ),
              );
              return;
            }

            // update steps
            updatedStep = addComponentsAtIndex({
              index: updateIndex,
              configArray: updatedStep.config.components,
              componentConfigs: [originalComponentConfig],
              step: updatedStep,
            });
          } else {
            const { componentConfig, componentFields } = createComponentConfigAndFields({
              itemConfig: item,
              // both initial and form value fieldSchemas are used to avoid
              // collisions with field keys which may be deleted prior to save.
              fieldKeys: uniq([...fieldSchema, ...initialFieldSchema].map(({ key }) => key)),

              step,
            });
            newComponentConfig = componentConfig;
            newComponentFields = componentFields;

            if (hoveredIndex == null) {
              // footer component should always be the last in the stack
              // update steps

              updatedStep = set(
                ['config', 'components'],
                [
                  ...nonFooterComponentsConfig.slice(0, defaultShiftIndex),
                  newComponentConfig,
                  ...nonFooterComponentsConfig.slice(defaultShiftIndex),
                  ...footerComponentConfig,
                ],

                step,
              );
            } else if (firstHoveredIndex != null) {
              const updateIndex = determineComponentShiftIndex({
                hoverComponentIndex: parseInt(firstHoveredIndex),
                hoverPosition,
              });

              if (
                (isUiParentComponentConfig(item as UiComponentConfig) ||
                  isUiTransitionComponentConfig(item as UiComponentConfig)) &&
                isInsertingComponentBetweenBIPAComponents({ step, index: updateIndex })
              ) {
                dispatch(
                  showMessage(
                    'Cannot insert another button between BIPA text and submit button',
                    MessageType.Failure,
                  ),
                );
                return;
              }

              // update steps
              updatedStep = addComponentsAtIndex({
                index: updateIndex,
                componentConfigs: [newComponentConfig],

                configArray: step.config.components,

                step,
              });
            }
            if (updatedStep) {
              // insert component preview
              const updatedComponentConfigPath = findComponentConfigPath(
                (component: UiComponentConfig) => component.name === newComponentConfig?.name,
                updatedStep.config.components,
              );
              if (updatedComponentConfigPath) {
                dispatch(
                  refreshComponentPreview(
                    updatedStep.name,
                    updatedComponentConfigPath.join('.'),
                    newComponentConfig,
                  ),
                );
              }
            }
          }

          if (updatedStep) {
            updateSteps({
              updatedStep,

              ...(newComponentConfig ? { component: newComponentConfig } : {}),
              ...(newComponentFields.length
                ? { fields: fieldSchema.concat(newComponentFields) }
                : {}),
            });
          }
          setHoveredIndex(null);
          setHoverPosition(null);
        },
      }),
      [hoveredIndex, step, updateSteps, components, hoverPosition],
    );

    const isNewComponent = useMemo(
      () => (isOver ? componentItem.name == null : false),
      [componentItem, isOver],
    );

    const isNewFooterComponent = useMemo(
      () =>
        isOver
          ? componentItem.name == null && componentItem.type === UiComponentConfigType.Footer
          : false,
      [componentItem, isOver],
    );

    const serializedTheme = serializeTheme({
      id: 'mockTheme',
      type: ApiModel.Theme,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      attributes: theme,
    });

    const backgroundColor =
      colorCss(stepPreview?.styles?.['background-color']?.base?.base) ??
      serializedTheme.colorModalBackground;

    const showHeaderComponent = isHeaderComponentVisible(step, serializedTheme.logoUrl);
    // subset of conditions in /inquiry/components/ConnectedNavbar.tsx
    const showCloseButton = cancelButtonEnabledForStep(step) && !isTerminalStep(step);

    const headerComponent = (
      <Navbar
        onBack={noop}
        logoUrl={serializedTheme.logoUrl}
        logoDisplay={serializedTheme.navbarLogoDisplay}
        colorIconHeader={colorCss(stepPreview?.styles?.['text-color']?.['header-button']?.base)}
        styles={{
          backgroundColor: colorCss(stepPreview?.styles?.['background-color']?.header?.base),
          borderColor: colorCss(stepPreview?.styles?.['border-color']?.header?.base),
          borderWidth: sizeCss(stepPreview?.styles?.['border-width']?.header?.base?.bottom),
        }}
        onClose={noop}
        showCloseButton={showCloseButton}
        showBackButton={step?.config?.['back-step'] != null}
      />
    );

    const mockInquiryForScreen = createMockInquiryPropsForUiComponentEditor(stepPreview, dispatch);

    const buildComponents = useCallback(
      ({ predicate }: { predicate: (component: UiComponent) => boolean }) => {
        if (!isUiStepScreen || components == null) return null;
        return components.filter(predicate).map((component: UiComponent, idx: number) => {
          return (
            <ScreenComponent
              key={`${component.name}-${idx}`}
              index={idx}
              component={component}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              stepPreview={stepPreview}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              step={step}
              fieldSchema={fieldSchema}
              initialFieldSchema={initialFieldSchema}
              editable={editable}
              selectedComponentAndStep={selectedComponentAndStep}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              hoveredIndex={hoveredIndex}
              setHoveredIndex={setHoveredIndex}
              hoverPosition={hoverPosition}
              setHoverPosition={setHoverPosition}
              handleOnClick={onComponentClick}
              updateSteps={updateSteps}
              canNestComponents={isUiParentComponent(component)}
              mockInquiry={mockInquiryForScreen}
            />
          );
        });
      },
      [
        isUiStepScreen,
        components,
        step,
        stepPreview,
        fieldSchema,
        editable,
        selectedComponentAndStep,
        hoveredIndex,
        hoverPosition,
        onComponentClick,
        updateSteps,
      ],
    );

    const screenComponents = buildComponents({
      predicate: (component: UiComponent) => component.type !== UiComponentType.Footer,
    });

    const footerComponent = buildComponents({
      predicate: (component: UiComponent) => component.type === UiComponentType.Footer,
    })?.[0];

    return (
      <DragAndDropProvider>
        <ThemeProvider theme={serializedTheme}>
          <ThemedFonts>
            <ScreenEditorDroppableScreenContainer
              selected={selected && selectedComponentAndStep == null}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              backgroundColor={backgroundColor}
              startedComponentDrag={startedComponentDrag}
              disableDrop={startedComponentDrag && !isUiStepScreen}
              isOver={isOver}
              screenDimensions={screenDimensions}
            >
              {isUiStepScreen && (
                <>
                  {['top', 'left', 'right'].map(
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    (side: Exclude<DraggableHoverPosition, 'bottom'>, idx: number) => (
                      <ScreenIndicator side={side} isOver={isOver} key={`${side}-${idx}`} />
                    ),
                  )}
                </>
              )}
              <ScreenDropzone
                ref={dropRef}
                isOver={isOver}
                data-test={`screen-editor-screen-dropzone__${step?.name}`}
              >
                {!isUiStepScreen && fallbackScreen}
                {isUiStepScreen && (
                  <ScreenDropzoneInner>
                    {showHeaderComponent && (
                      <NavbarContainer
                        isHovering={isHovering}
                        onMouseOver={() => {
                          setIsHovering();
                        }}
                        onMouseOut={() => {
                          setStopHovering();
                        }}
                      >
                        {headerComponent}
                      </NavbarContainer>
                    )}
                    <ScreenComponentContainer>
                      {screenComponents}
                      {isNewComponent && !isNewFooterComponent && (
                        <ScreenDropPreview isOver={isOverCurrent} />
                      )}
                    </ScreenComponentContainer>
                    {footerComponent == null && isNewFooterComponent && (
                      <ScreenDropPreview isOver={isOver} />
                    )}
                    {footerComponent != null && <div>{footerComponent}</div>}
                  </ScreenDropzoneInner>
                )}
              </ScreenDropzone>
            </ScreenEditorDroppableScreenContainer>
          </ThemedFonts>
        </ThemeProvider>
      </DragAndDropProvider>
    );
  },
);

ScreenEditorDroppableScreen.displayName = 'ScreenEditorDroppableScreen';
